'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

import { StickySearchContext } from '@/components/search/sticky-search-context';
import { searchFieldRadiusClass } from '@/components/search/search-config';

/** Viewport max-width matching Tailwind `lg` (1024px). */
const MOBILE_MEDIA = '(max-width: 1023px)';

/**
 * Scroll-padding on the document root so `scrollIntoView` and scroll targets
 * clear the fixed mobile bar when the window is the scrollport.
 */
const STICKY_SCROLL_PADDING_TOP =
  'calc(5.5rem + env(safe-area-inset-top, 0px))';

export const STICKY_SEARCH_ANCHOR_ID = 'homepage-search';

/** Sticky bar only after the sentinel has scrolled above the viewport, not when it is still below. */
export const shouldShowStickySearchBar = (
  entry: Pick<
    IntersectionObserverEntry,
    'isIntersecting' | 'boundingClientRect'
  >
): boolean => !entry.isIntersecting && entry.boundingClientRect.top < 0;

type StickySearchWrapperProps = {
  children: React.ReactNode;
  /** The search component to render in the sticky bar. */
  stickySearchContent: React.ReactNode;
  /** When true, only render the mobile sticky bar (listings page). */
  mobileOnly?: boolean;
};

export const StickySearchWrapper = ({
  children,
  stickySearchContent,
  mobileOnly = false,
}: StickySearchWrapperProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(false);

  const stickySearchContext = useMemo(
    () => ({ mobileExpanded, setMobileExpanded }),
    [mobileExpanded]
  );

  useEffect(() => {
    if (!showStickyBar) {
      setMobileExpanded(false);
    }
  }, [showStickyBar]);

  useEffect(() => {
    const scrollRoot = document.documentElement;
    const mql = window.matchMedia(MOBILE_MEDIA);
    const apply = () => {
      if (mql.matches && showStickyBar) {
        scrollRoot.style.scrollPaddingTop = STICKY_SCROLL_PADDING_TOP;
      } else {
        scrollRoot.style.scrollPaddingTop = '';
      }
    };
    apply();
    mql.addEventListener('change', apply);
    return () => {
      mql.removeEventListener('change', apply);
      scrollRoot.style.scrollPaddingTop = '';
    };
  }, [showStickyBar]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const mql = window.matchMedia(MOBILE_MEDIA);
    const syncStickyBar = (entry: IntersectionObserverEntry) => {
      if (mobileOnly && !mql.matches) {
        setShowStickyBar(false);
        return;
      }
      setShowStickyBar(shouldShowStickySearchBar(entry));
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) {
          syncStickyBar(entry);
        }
      },
      {
        root: null,
        threshold: 0,
        rootMargin: '0px 0px 0px 0px',
      }
    );
    observer.observe(el);

    const handleViewportChange = () => {
      if (mobileOnly && !mql.matches) {
        setShowStickyBar(false);
      }
    };
    mql.addEventListener('change', handleViewportChange);

    return () => {
      observer.disconnect();
      mql.removeEventListener('change', handleViewportChange);
    };
  }, [mobileOnly]);

  return (
    <>
      <div ref={sentinelRef} id={STICKY_SEARCH_ANCHOR_ID}>
        {children}
      </div>

      {/* Mobile sticky bar - fixed to top */}
      <div
        className={clsx(
          'fixed inset-x-0 top-0 z-40 w-full lg:hidden',
          showStickyBar &&
            mobileExpanded &&
            clsx('bg-white shadow-md', searchFieldRadiusClass('shell-bottom')),
          'transition-transform duration-300 ease-out',
          showStickyBar
            ? 'translate-y-0 animate-fade-in'
            : 'pointer-events-none -translate-y-full opacity-0'
        )}
      >
        <StickySearchContext.Provider value={stickySearchContext}>
          <div
            className={clsx(
              mobileExpanded
                ? 'px-4 pb-4 pt-[max(0.75rem,env(safe-area-inset-top))]'
                : 'px-2 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))]'
            )}
          >
            {stickySearchContent}
          </div>
        </StickySearchContext.Provider>
      </div>

      {/* Desktop sticky toolbar - fixed to top */}
      {!mobileOnly && (
        <div
          className={clsx(
            'fixed inset-x-0 top-0 z-30 hidden lg:block',
            showStickyBar &&
            clsx('bg-white shadow-md', searchFieldRadiusClass('shell-bottom')),
            'transition-transform duration-300 ease-out',
            showStickyBar
              ? 'translate-y-0 animate-fade-in'
              : 'pointer-events-none -translate-y-full opacity-0'
          )}
        >
          <div className="mx-auto flex h-[82px] max-w-screen-2xl items-center justify-center px-4">
            {stickySearchContent}
          </div>
        </div>
      )}
    </>
  );
};
